/**
 * Connect-wizard pure logic — register→tag mapping, datapoint suggestion, and
 * the cloud commit (create the device Thing + tags, then bind them to an
 * equipment's ports).
 *
 * Kept as a plain `.ts` module (no JSX) so the component files stay
 * component-only (no react-refresh boundary noise) and this logic is unit-
 * testable in isolation. `nameScore` + `suggestTagForParam` are lifted verbatim
 * from CommissionWizard (they are module-private there) so the Connect flow can
 * reuse the exact same name/semantic matching the Commission flow uses.
 */

import type { DataKind, EntityDef, ParameterDef } from "@foundry/foundry-plant";
import { compatibleKinds } from "@/components/twin/TagSearchCombobox";
import {
  things,
  twinBindings,
  type InstrumentParamInput,
  type TagOption,
  type ThingDataType,
  type ThingTagInput,
  type TwinBinding,
} from "@/lib/api";
import { blankRow, tagInputToRow, type TagRow } from "@/components/iot/ThingTagRows";
import type { DiscoveredDevice, SniffedRegister } from "@/lib/gateway-client";
import { facadeForPointType, registerKey, type PickedPoint } from "@/lib/iot/points";
import { isModbusDriver } from "@/lib/iot/drivers";
import { linkPrefill } from "@/lib/iot/drivers";
import {
  buildV1Body,
  buildV1Params,
  buildV2Body,
  legacyRegisterText,
  modbusDataType,
  parseRegisterRef,
  rowPollSec,
  rowsToPoints,
} from "@/lib/iot/instrument-body";
import { errorDetailOf, fieldProblemsOf, toFormFieldErrors, writeErrorMessage } from "@/lib/api/error";

/** Default poll interval seeded onto rows + the created instrument. */
export const CONNECT_POLL_DEFAULT = 5;

/**
 * The created instrument's own poll interval, from the Map step's per-row
 * "poll" values: the smallest whole-second value among the named rows, else
 * {@link CONNECT_POLL_DEFAULT}. Each point is also sent its own row's value
 * (instrument-body rowsToPoints / buildV1Params); the body's is the default for
 * a point without one, and the floor an older gateway that ignores per-point
 * rates polls everything at (the server's config push keeps it the smallest).
 */
export function instrumentPollSec(rows: TagRow[]): number {
  let best = 0;
  for (const r of rows) {
    if (!r.name.trim()) continue;
    const s = rowPollSec(r);
    if (s !== undefined && (best === 0 || s < best)) best = s;
  }
  return best || CONNECT_POLL_DEFAULT;
}

/** A sniffed/templated register → the `ThingTagInput` the row editor seeds from. */
export function registerToTagInput(reg: SniffedRegister): ThingTagInput {
  const bit = reg.space === "coil" || reg.space === "discrete";
  if (bit) {
    // A coil / discrete input (listed by a v2 browse) onboards as a boolean state.
    return {
      name: reg.guessed_name,
      data_type: "boolean",
      unit: reg.unit,
      address: legacyRegisterText(reg.address),
      kind: reg.space,
      data_kind: "boolean_state",
      p_type: 1,
      reg_count: 1,
    };
  }
  const isFloat = reg.data_type === "float32" || reg.data_type === "double";
  const scaled = reg.scale !== undefined && reg.scale !== 1;
  // A scaled integer (age x 0.1 s) reads as a number, not a count.
  const facade: ThingDataType = isFloat || scaled ? "number" : "integer";
  // Legacy p_type codes (mig 0079): 5 = double, 4 = float32, 3 = int32, 1 = int16.
  // A double coded as 4 would be decoded by the gateway as a float32 (garbage).
  const p_type =
    reg.data_type === "double" ? 5 : reg.data_type === "float32" ? 4 : reg.data_type === "int32" ? 3 : 1;
  return {
    name: reg.guessed_name,
    data_type: facade,
    unit: reg.unit,
    // reg.address is the 0-based register; written so parseRegisterRef reads the same one back.
    address: legacyRegisterText(reg.address),
    p_type,
    reg_count: reg.reg_count,
    kind: reg.space === "input" ? "input" : undefined,
    is_cycle_counter: /cycle|count/i.test(reg.guessed_name),
    ...(scaled ? { scale: reg.scale } : {}),
    ...(reg.reg_count >= 2 && reg.word_order ? { word_order: reg.word_order } : {}),
    ...(reg.poll_sec !== undefined ? { poll_sec: reg.poll_sec } : {}),
  };
}

/**
 * A ticked register → its Map-step row: {@link registerToTagInput} plus the
 * unsigned hint (uint16 / uint32), which a v2 gateway reads the value as, and
 * an imported map's deadband and heartbeat (a plain Thing tag has neither, so
 * they ride the row).
 */
export function registerToTagRow(reg: SniffedRegister, index: number, pollDefault: number): TagRow {
  const unsigned = reg.unsigned && reg.space !== "coil" && reg.space !== "discrete";
  return {
    ...tagInputToRow(registerToTagInput(reg), `reg_${registerKey(reg)}_${index}`, pollDefault),
    point_type: unsigned ? (reg.data_type === "int32" ? "uint32" : "uint16") : undefined,
    ...(reg.deadband !== undefined ? { deadband: reg.deadband } : {}),
    ...(reg.max_silence_sec !== undefined ? { max_silence_sec: reg.max_silence_sec } : {}),
  };
}

/**
 * A point picked on a non-Modbus device → a Map-step row carrying its driver
 * address and data type. A `string` point gets data_kind `string_label`, which
 * the ingester needs (it dead-letters a string reading without it). An imported
 * map's poll interval, deadband and heartbeat come along.
 */
export function pointToTagRow(p: PickedPoint, index: number, pollDefault: number): TagRow {
  return {
    ...blankRow(pollDefault),
    key: `pt_${index}_${p.metric}`,
    name: p.metric,
    data_type: facadeForPointType(p.data_type),
    address: "",
    point_address: p.address,
    point_type: p.data_type,
    data_kind: p.data_type === "string" ? "string_label" : undefined,
    unit: p.unit ?? "",
    ...(p.scale !== undefined && p.scale !== 1 ? { scale: p.scale } : {}),
    ...(p.poll_sec !== undefined ? { poll_sec: String(p.poll_sec) } : {}),
    ...(p.deadband !== undefined ? { deadband: p.deadband } : {}),
    ...(p.max_silence_sec !== undefined ? { max_silence_sec: p.max_silence_sec } : {}),
  };
}

/** A TagRow's Modbus wire type for the createInstrument param map (lib/iot/instrument-body). */
export { modbusDataType };

/** Facade data_type → canonical data_kind, so pseudo-tags built from rows are
 *  compatibility-checkable against an entity port via {@link compatibleKinds}. */
export function dataTypeToDataKind(dt: ThingDataType): string {
  switch (dt) {
    case "number":
      return "analog_scalar";
    case "integer":
      return "count_scalar";
    case "percentage":
      return "analog_percentage";
    case "boolean":
      // The canonical kind a state port takes (compatibleKinds("boolean_state") accepts only it).
      return "boolean_state";
    case "category":
      return "categorical";
    case "text":
      return "string";
    case "array":
      return "distribution";
    case "batch":
      return "event";
    default:
      return "analog_scalar";
  }
}

/** Named rows → pseudo `TagOption`s (id = row.key) for the suggestion + picker. */
export function pseudoTagsFromRows(rows: TagRow[]): TagOption[] {
  return rows
    .filter((r) => r.name.trim().length > 0)
    .map((r) => ({
      id: r.key,
      device_id: "",
      name: r.name.trim(),
      unit: r.unit,
      data_kind: dataTypeToDataKind(r.data_type),
      semantic_tag: null,
    }));
}

/** The entity's bindable datapoints — parameters carrying a dataKind (same rule
 *  the Equipment workspace + CommissionWizard use). */
export function bindablePorts(entity: EntityDef): (ParameterDef & { dataKind: DataKind })[] {
  return entity.parameters.filter(
    (p): p is ParameterDef & { dataKind: DataKind } => !!p.dataKind,
  );
}

/** Lightweight token-similarity in [0,1] between a datapoint name and a tag.
 *  Lifted from CommissionWizard.nameScore. */
export function nameScore(paramName: string, tag: TagOption): number {
  const p = paramName.toLowerCase();
  const candidates = [tag.semantic_tag ?? "", tag.name ?? ""]
    .map((s) => s.toLowerCase())
    .filter(Boolean);
  let best = 0;
  for (const c of candidates) {
    if (c === p) return 1;
    if (c.includes(p) || p.includes(c)) best = Math.max(best, 0.85);
    const pt = new Set(p.split(/[^a-z0-9]+/).filter(Boolean));
    const ct = new Set(c.split(/[^a-z0-9]+/).filter(Boolean));
    const shared = [...pt].filter(
      (t) => ct.has(t) || [...ct].some((x) => x.startsWith(t) || t.startsWith(x)),
    );
    if (pt.size > 0) best = Math.max(best, (shared.length / pt.size) * 0.8);
  }
  return best;
}

/** Best-matching row (as a pseudo-tag) for a datapoint. Lifted from
 *  CommissionWizard.suggestTagForParam. */
export function suggestTagForParam(
  param: ParameterDef & { dataKind: DataKind },
  tags: TagOption[],
): { tag: TagOption; score: number } | null {
  const allowed = compatibleKinds(param.dataKind);
  let best: { tag: TagOption; score: number } | null = null;
  for (const tag of tags) {
    if (!allowed.has(tag.data_kind as DataKind)) continue;
    const score = nameScore(param.name, tag);
    if (score < 0.34) continue;
    if (!best || score > best.score) best = { tag, score };
  }
  return best;
}

/** Named rows carrying an address → the createInstrument v1 Modbus param map. */
export function buildInstrumentParams(rows: TagRow[]): InstrumentParamInput[] {
  return buildV1Params(rows);
}

/** The register_map.json contract a mock/plant gateway ships (its "units" section). */
export interface RegisterMap {
  units: Record<
    string,
    {
      unit_id: number;
      equipment_key: string;
      registers: Array<{
        address: number;
        kind: string; // "holding" | "input" | "coil" | "discrete"
        metric: string;
        type: string; // u16 | i16 | u32 | i32
        scale: number;
        offset: number;
        role: string; // param | trigger | count | heartbeat
        words: number;
        data_kind: string; // analog_scalar | coil_bit | count_scalar | boolean_state | ...
        unit?: string;
      }>;
    }
  >;
}

/**
 * Map one unit of a mock/plant `register_map.json` into editor rows — the "load register map"
 * import that ends the hand-naming of `hr_N`. Each register keeps its metric name verbatim
 * (it is the equipment's operating_modes tag_hint, so trigger/count/coil tags bind to its
 * state machine), and carries its register space + scale + data_kind so a coil onboards as a
 * boolean_state tag and scaled values round-trip. `coil_bit` is normalised to `boolean_state`
 * (the DB vocabulary).
 */
export function registerMapToRows(map: RegisterMap, unitId: number | string): TagRow[] {
  const u = map.units?.[String(unitId)];
  if (!u) return [];
  const SPACES = ["holding", "input", "coil", "discrete"];
  return u.registers.map((r, i): TagRow => {
    const isCoil = r.kind === "coil" || r.kind === "discrete";
    const isFloat = r.type === "float32" || r.type === "double";
    const facade: ThingDataType = isCoil
      ? "boolean"
      : r.words >= 2 || r.role === "count"
        ? "integer"
        : isFloat
          ? "number"
          : "integer";
    return {
      key: `rm-${unitId}-${i}`,
      name: r.metric,
      data_type: facade,
      unit: r.unit ?? "",
      poll_sec: "",
      address: String(r.address),
      min: "",
      max: "",
      p_type: r.type === "double" ? 5 : r.type === "float32" ? 4 : r.words >= 2 ? 3 : 1,
      reg_count: r.words,
      register_kind: (SPACES.includes(r.kind) ? r.kind : "holding") as TagRow["register_kind"],
      scale: r.scale,
      offset: r.offset,
      data_kind: r.data_kind === "coil_bit" ? "boolean_state" : r.data_kind,
      is_cycle_counter: r.role === "count" && /cycle|count/i.test(r.metric) ? true : undefined,
    };
  });
}

export interface CommitParams {
  gatewayId: string;
  device: DiscoveredDevice;
  rows: TagRow[];
  entityId: string;
  /** port name → row.key (the binding plan). */
  binds: Record<string, string>;
  /** The protocol picked on the Protocol step (defaults to the device's). */
  driver?: string;
  /** The device's link params from the connection form (defaults to what the scan found). */
  link?: Record<string, unknown>;
  /**
   * Create with the legacy v1 body: a Modbus device on a gateway without config
   * schema v2, or a server with no drivers registry (see needsV1Body).
   * Otherwise the v2 body (`link` + per-point `address`).
   */
  v1?: boolean;
}

export interface CommitResult {
  thingId: string;
  slug: string;
  configPushed: boolean;
  /** Bindings actually created. */
  bound: number;
  /** Bindings planned (chosen ports with a mapped row). */
  planned: number;
  /** Planned bindings whose new tag could not be found after the create, so
   *  those ports were left on their previous source. */
  unresolved?: number;
  /** Earlier bindings of the bound ports that this go-live replaced and removed: a simulated tag, a template
   *  placeholder, or a device that is no longer the port's (the "light up the existing equipment" re-point).
   *  An imported history or a manual entry is never one of them (see {@link splitPriorBindings}). */
  repointed?: number;
  /** Imported-history bindings left in place on the bound ports, so each port still reads its history where the
   *  live tag has no readings (the live source ranks first). Absent when the bindings could not be listed. */
  keptHistory?: number;
  /** Manual-entry bindings (a tag typed in by hand or measured with a handheld probe) left in place on the bound
   *  ports, for the same reason. Absent when the bindings could not be listed. */
  keptManual?: number;
  /** True when synthesised in stub mode (no cloud write happened). */
  simulated?: boolean;
}

/**
 * The created instrument's tags, keyed by the metric each one carries. The
 * backend scopes an instrument tag's NAME as "<instrument>.<metric>" (so two
 * instruments that both expose "result" cannot collide) and keeps the bare
 * metric in `semantic_tag`, so a row named "pour_temperature" is found under
 * either form. Matching the bare row name against `name` alone finds nothing.
 */
export function tagIdsByMetric(
  items: { id: string; name: string; semantic_tag?: string | null }[],
  instrumentName: string,
): (metric: string) => string | undefined {
  const byKey = new Map<string, string>();
  for (const t of items) {
    const bare = t.semantic_tag?.trim();
    if (bare) byKey.set(bare, t.id);
    byKey.set(t.name, t.id);
  }
  const scope = instrumentName.trim();
  return (metric) => {
    const m = metric.trim();
    return byKey.get(m) ?? (scope ? byKey.get(`${scope}.${m}`) : undefined);
  };
}

/**
 * What go-live does with the bindings a bound port already had, once the live tag is bound beside them.
 *
 * Going live used to remove every other binding on a bound port, so a port that had been fed by an import lost its
 * history: the readings stayed stored, but nothing reached them through the port. A port has one series built from
 * all its sources (the live source ranks first, then the machine's history, then a report, then manual entry), so an
 * import's binding and a manual entry's now stay, and the port reads live wherever live has readings and the others
 * before. Everything else on the port is replaced by the live tag, as before: a simulated tag, a template placeholder,
 * a device that is no longer the port's.
 *
 * Both are told by the tag's `source`, which the bindings list carries: 'import' for every tag a data source's reader
 * wrote, and 'manual' (typed in by hand) or 'ble' (measured with a handheld probe) for a tag on the manual entry
 * gateway, the Thing the port ranks as manual entry.
 *
 * Only the ports in `livePorts` are touched, and the live tags themselves never are.
 */
export function splitPriorBindings(
  existing: readonly TwinBinding[],
  livePorts: ReadonlySet<string>,
  liveTagIds: ReadonlySet<string>,
): { replace: TwinBinding[]; imported: TwinBinding[]; manual: TwinBinding[] } {
  const prior = existing.filter((b) => livePorts.has(b.port) && !liveTagIds.has(b.tag_id));
  const isManual = (b: TwinBinding) => b.source === "manual" || b.source === "ble";
  return {
    replace: prior.filter((b) => b.source !== "import" && !isManual(b)),
    imported: prior.filter((b) => b.source === "import"),
    manual: prior.filter(isManual),
  };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The go-live toast, in the plant's words: how many ports are bound, how many earlier tags were replaced, and how many
 * imported histories and manual entries stay with their ports. A count of nothing is left out.
 */
export function goLiveToast(
  equipmentName: string,
  res: Pick<CommitResult, "bound" | "repointed" | "keptHistory" | "keptManual">,
): string {
  if (res.bound <= 0) return "Device is live.";
  const parts = [plural(res.bound, "port bound", "ports bound")];
  if (res.repointed) parts.push(`${plural(res.repointed, "earlier tag", "earlier tags")} replaced`);
  if (res.keptHistory) parts.push(`${plural(res.keptHistory, "imported history", "imported histories")} kept`);
  if (res.keptManual) parts.push(`${plural(res.keptManual, "manual entry", "manual entries")} kept`);
  return `${equipmentName.trim() || "Equipment"} is live: ${parts.join(", ")}.`;
}

/**
 * The live commit: create the discovered device as a CHILD instrument Thing on
 * the chosen gateway (which also config_pushes so the device starts polling),
 * resolve the created tag ids by metric, then POST one binding per chosen
 * (port → mapped register). Returns the created Thing's id/slug + counts so the
 * Go-live step can verify (measure) and flag the slug-equality gotcha.
 */
export async function commitConnection({
  gatewayId,
  device,
  rows,
  entityId,
  binds,
  driver,
  link,
  v1 = true,
}: CommitParams): Promise<CommitResult> {
  const drv = driver ?? device.driver;
  const instrumentName = device.name?.trim() || `PLC ${device.ip}`;
  const params = link ?? linkPrefill(drv, device);
  const pollSec = instrumentPollSec(rows);
  // The only protocol-specific part of go-live: the create body. Everything
  // after it (tags by metric → bindings → re-point) is the same for every driver.
  const created = v1
    ? await things.createInstrument(
        gatewayId,
        buildV1Body({ name: instrumentName, driver: drv, link: params, rows, pollSec }),
      )
    : await things.createInstrumentV2(
        gatewayId,
        buildV2Body({ name: instrumentName, driver: drv, link: params, rows, pollSec }),
      );

  // Resolve the freshly-created tag ids (create doesn't echo them).
  let tagIdFor: (metric: string) => string | undefined = () => undefined;
  try {
    // Every tag: an instrument can carry more than the server's first page of 50.
    tagIdFor = tagIdsByMetric(await things.allTags(created.instrument_id), instrumentName);
  } catch {
    // Non-fatal: the device + config still landed; the bindings are reported as
    // unresolved below instead of silently vanishing.
  }

  const intended = Object.entries(binds).flatMap(([port, rowKey]) => {
    const row = rows.find((r) => r.key === rowKey);
    return row ? [{ port, tagId: tagIdFor(row.name) }] : [];
  });
  const planned = intended.filter((b): b is { port: string; tagId: string } => !!b.tagId);

  let bound = 0;
  let repointed = 0;
  let keptHistory: number | undefined;
  let keptManual: number | undefined;
  if (planned.length > 0) {
    const results = await Promise.allSettled(
      planned.map((b) =>
        twinBindings.create({ entity_id: entityId, port: b.port, tag_id: b.tagId }),
      ),
    );
    bound = results.filter((r) => r.status === "fulfilled").length;

    // Re-point: this equipment's ports were already bound to the modeled (usually
    // simulated) tags. Now that the real device's tags are bound to those same ports,
    // replace the PRIOR binding on each bound port by the real tag: a simulated tag, a
    // template placeholder or an earlier device is unbound (the tag itself is left
    // orphaned, not deleted). An IMPORT's binding and a MANUAL ENTRY's stay: the port keeps
    // the history and the entries it was fed, the live source ranks first, and the port
    // reads live wherever live has readings (splitPriorBindings). Only a port whose live
    // binding landed is touched, so a port that could not be bound keeps what it had.
    // Best-effort: a lingering sim binding is cosmetic, never a reason to fail a completed
    // go-live.
    try {
      const live = planned.filter((_, i) => results[i].status === "fulfilled");
      if (live.length > 0) {
        const existing = await twinBindings.list(entityId);
        const { replace, imported, manual } = splitPriorBindings(
          existing.items,
          new Set(live.map((b) => b.port)),
          new Set(live.map((b) => b.tagId)),
        );
        keptHistory = imported.length;
        keptManual = manual.length;
        const removed = await Promise.allSettled(replace.map((b) => twinBindings.remove(b.id)));
        repointed = removed.filter((r) => r.status === "fulfilled").length;
      }
    } catch {
      // non-fatal
    }
  }

  return {
    thingId: created.instrument_id,
    slug: created.slug,
    configPushed: created.config_pushed,
    bound,
    planned: intended.length,
    unresolved: intended.length - planned.length,
    repointed,
    keptHistory,
    keptManual,
  };
}

/**
 * A failed go-live create as one operator-facing line. A 422's fields[] are
 * named by what the operator sees: the connection field, or the point's metric
 * (params[i] is the i-th point sent, in Map order).
 */
export function goLiveErrorMessage(err: unknown, driver: string, rows: TagRow[]): string {
  const f = toFormFieldErrors(fieldProblemsOf(err));
  const parts: string[] = [];
  for (const [field, msg] of Object.entries(f.link)) parts.push(`connection ${field}: ${msg}`);
  const points = rowsToPoints(driver, rows).points;
  for (const [i, fields] of Object.entries(f.points)) {
    const metric = points[Number(i)]?.metric ?? `point ${Number(i) + 1}`;
    for (const [field, msg] of Object.entries(fields)) parts.push(`${metric}${field ? ` ${field}` : ""}: ${msg}`);
  }
  parts.push(...f.other);
  if (parts.length > 0) {
    const lead = errorDetailOf(err) ?? "The device could not be added";
    return `${lead.replace(/[.\s]+$/, "")}. ${parts.join("; ")}.`;
  }
  return writeErrorMessage(err, "Go-live failed.");
}

/** A point the server refused at go-live: the Map row it came from and why. */
export interface RefusedPoint {
  key: string;
  name: string;
  message: string;
}

/**
 * A refused go-live create's per-point errors, one per Map row. The server
 * names points by index (params[i]) in the order they were sent, which is the
 * named rows (less a Modbus row whose register does not parse, never sent).
 */
export function goLiveRowErrors(err: unknown, driver: string, rows: TagRow[]): RefusedPoint[] {
  const f = toFormFieldErrors(fieldProblemsOf(err));
  const sent = rows.filter(
    (r) => r.name.trim().length > 0 && (!isModbusDriver(driver) || parseRegisterRef(r.address) !== null),
  );
  return Object.entries(f.points).flatMap(([i, fields]) => {
    const row = sent[Number(i)];
    if (!row) return [];
    const message = Object.entries(fields)
      .map(([field, msg]) => (field ? `${field}: ${msg}` : msg))
      .join("; ");
    return [{ key: row.key, name: row.name.trim(), message }];
  });
}

/** A slug guess for the stub-mode result summary (mirrors the server's slugify). */
export function slugish(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
